import { toWesternDigits } from './text';

/**
 * Phone numbers as arqam reads them (its doc 59, `app_normalize_phone`):
 * Moroccan numbers in any usual form, checked strictly; foreign numbers only
 * with «+» or «00» and their country code, 8 to 15 digits in all (E.164).
 * The apps check first so that the member hears why, in Arabic.
 */

/** A hint for every phone field. */
export const PHONE_HINT =
  'للأرقام الأجنبية: ابدأ بـ + ورمز البلد، مثلاً +33 6 12 34 56 78';

/** The number in international form ("+212612345678"), or null. */
export function normalizePhone(raw: string | null | undefined): string | null {
  let d = toWesternDigits(raw ?? '').replace(/[\s().-]/g, '');
  if (d === '' || /^\+?0*$/.test(d)) return null;
  // «00» is the international prefix: same as «+».
  if (/^00[1-9]/.test(d)) d = `+${d.slice(2)}`;
  // Morocco: +212 then 5–8 and 8 digits; local 0… or 9 digits.
  if (/^\+2120[5-8]\d{8}$/.test(d)) return `+212${d.slice(5)}`;
  if (/^\+212[5-8]\d{8}$/.test(d)) return d;
  if (/^\+212/.test(d)) return null; // a Moroccan number with a typo
  if (/^212[5-8]\d{8}$/.test(d)) return `+${d}`;
  if (/^0[5-8]\d{8}$/.test(d)) return `+212${d.slice(1)}`;
  if (/^[5-8]\d{8}$/.test(d)) return `+212${d}`;
  // Other countries, in international form only.
  if (/^\+[1-9]\d{7,14}$/.test(d)) return d;
  return null;
}

/**
 * A phone number as typed, cleaned for the API ("٠٦ 12.34" → "0612…"), or
 * why it can't be one. arqam stores it in international form. Empty = no
 * number.
 */
export function cleanPhone(
  input: string,
): { phone: string | null } | { error: string } {
  const phone = toWesternDigits(input).replace(/[\s.\-()]/g, '');
  if (!phone) return { phone: null };
  if (normalizePhone(phone)) return { phone };
  if (/^(?:\+212|00212|212|0)/.test(phone)) {
    return { error: 'رقم مغربي غير صحيح. مثال: 0612345678' };
  }
  if (/^(?:\+|00)/.test(phone)) {
    return {
      error: 'رقم أجنبي غير صحيح: + ورمز البلد ثم الرقم، من 8 إلى 15 رقماً.',
    };
  }
  return {
    error: `رقم غير صحيح. مثال: 0612345678. ${PHONE_HINT}.`,
  };
}

/**
 * A number in any form as digits with the country code ("212602…",
 * "33612…"), for wa.me and tel: links; null when it can't be read.
 */
export function internationalPhone(phone: string | null | undefined) {
  return normalizePhone(phone)?.slice(1) ?? null;
}
